import React, { useState, useRef, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'

import { getProfileName } from '/src/components/SingleSICE/Reusable/Helpers/sice-profile-helpers'
import SkeletonComponent from '/src/components/Reusable/SkeletonComponent'
import CompanyContactButton from '/src/components/SingleSICE/Reusable/CompanyContactButton'
import { amountFormatter } from '/src/lib/helpers'
import { HUBSPOT_FORM_SOURCE } from '/src/lib/constants'
import entityApi from '/src/apis/entityApi';

/* helpers */

const getProfileLogo = (investor) => {
    if (!investor) return null

    const imagePath =
		investor.logo_data?.path || investor.profile_pic_data?.path
    const alt = investor.name || 'Investor logo'

    if (imagePath) {
        return (
            <div className="logoBlock">
                <img src={imagePath} alt={alt} />
            </div>
        )
    }

    const initial = investor?.name?.substr(0, 1)?.toUpperCase() || '?'

    return (
        <div className="logoBlock">
            <h2>{initial}</h2>
        </div>
    )
}

/* title */

const HeaderTitles = ({ name, primaryType }) => (
    <div className="investor-profile-header__titles">
        <h1 className="investor-profile-header__name">{name}</h1>
    </div>
)

/* social */

const InvestorSocialLinks = ({ social, email, onContactClick, contactRef }) => {
    if (!social) return null

    const socialKeys = ['website', 'twitter', 'facebook', 'linkedin']

    const socialLinks = {}

    socialKeys.forEach((key) => {
        let val = social[key]
        if (!val || typeof val !== 'string') return

        // skip invalid handles like "@username"
        if (val[0] === '@') return

        // normalize urls
        if (
            val.substring(0, 3) === 'www' ||
			(val.indexOf('www') === -1 && val.indexOf('http') === -1)
        ) {
            val = `http://${val}`
        }

        socialLinks[key] = val
    })

    if (!Object.keys(socialLinks).length && !email && !onContactClick)
        return null

    return (
        <div className="investor-profile-header__social">
            <div className="investor-profile-header__social-icons">
                {Object.entries(socialLinks).map(([key, value]) => (
                    <a
                        key={key}
                        href={value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="investor-profile-header__social-icon"
                    >
                        {key === 'twitter' && <i className="fab fa-twitter" />}
                        {key === 'facebook' && (
                            <i className="fab fa-facebook-f" />
                        )}
                        {key === 'linkedin' && (
                            <i className="fab fa-linkedin-in" />
                        )}
                        {key === 'website' && <i className="fas fa-globe" />}
                    </a>
                ))}
            </div>

            {email && (
                <>
                    <span className="investor-profile-header__social-divider" />
                    <button
                        type="button"
                        className="btn btn-main-outline btn-sm investor-profile-header__email-button"
                        ref={contactRef}
                        onClick={onContactClick}
                    >
                        <i className="far fa-envelope" />
                        <span>Contact</span>
                    </button>
                </>
            )}

            {!email && onContactClick && (
                <>
                    <span className="investor-profile-header__social-divider" />
                    <button
                        type="button"
                        className="btn btn-main-outline btn-sm investor-profile-header__email-button"
                        onClick={onContactClick}
                    >
                        <i className="far fa-envelope" />
                        <span>Contact</span>
                    </button>
                </>
            )}
        </div>
    )
}

/* main component */

const InvestorProfileHeader = ({
    investor,
    user,
    owner,
    entityId,
    entityType,
    setState,
    page
}) => {
    const [showSkeleton, setShowSkeleton] = useState(true)
    const companyContactWrapperRef = useRef(null)
    const contactButtonRef = useRef(null)
    const [email, setEmail] = useState(null)

    const profileLogo = getProfileLogo(investor)
    const name = getProfileName(investor)
    const primaryType = investor?.type_data?.name

    const socialObject = {
        website: investor?.website,
        twitter: investor?.twitter,
        facebook: investor?.facebook,
        linkedin: investor?.linkedin
    }

    const isLoggedIn = !!user?.is_loggedin


    useEffect(() => {
        if (!isLoggedIn || (investor?.email_relations && email)) return
        setEmail(investor?.email_relations?.trim() || null)
    }, [investor])



    // choose which existing contact-sales popup variant to use
    // - anonymous users: "Discover the power of MAGNiTT" (single_profile_locked_contacts)
    // - logged-in but NO email: different popup (single_profile_locked_sections)
    // - logged-in WITH email: use page from parent as-is (only as fallback)
    const contactPage = !isLoggedIn
        ? HUBSPOT_FORM_SOURCE.single_profile_locked_contacts
        : !email
            ? HUBSPOT_FORM_SOURCE.single_profile_locked_sections
            : page

    const handleContactClick = () => {
        // If we already have an email, show a small popover with a clickable mailto link.
        if (
            email &&
			contactButtonRef.current &&
			typeof window !== 'undefined'
        ) {
            const $ = window.jQuery || window.$
            if ($ && $.fn && $.fn.popover) {
                const $btn = $(contactButtonRef.current)
                $btn.popover({
                    html: true,
                    content: `<a href="mailto:${email}" class="team-viewed-email">${email}</a>`,
                    placement: 'bottom',
                    container: 'body',
                    trigger: 'manual'
                })
                $btn.popover('toggle')
                return
            }

            // Fallback: open mail client directly
            window.location.href = `mailto:${encodeURIComponent(email)}`
            return
        }

        // If no email_relations, fall back to existing popup behavior via CompanyContactButton.
        if (!companyContactWrapperRef.current) return
        const target =
			companyContactWrapperRef.current.querySelector('button, a')
        if (target) {
            target.click()
        }
    }

    return (
        <div className="investor-profile-header">
            <div className="profile-logo-box">
                {showSkeleton ? (
                    <SkeletonComponent
                        SkeletonClassName="logoBlock-skeleton"
                        useTimeout
                        actionOnTimeout={() => setShowSkeleton(false)}
                    />
                ) : (
                    profileLogo
                )}
            </div>

            <div className="investor-profile-header__body">
                <div className="investor-profile-header__top">
                    <div className="investor-profile-header__left">
                        <HeaderTitles name={name} primaryType={primaryType} />
                    </div>
                    <div className="investor-profile-header__right">
                        <InvestorSocialLinks
                            social={socialObject}
                            email={email}
                            contactRef={contactButtonRef}
                            onContactClick={handleContactClick}
                        />
                    </div>
                </div>
                {!email && (
                    <div
                        ref={companyContactWrapperRef}
                        style={{ display: 'none' }}
                        aria-hidden="true"
                    >
                        <CompanyContactButton
                            user={user}
                            owner={owner}
                            id={entityId}
                            entityType={entityType}
                            setState={setState}
                            page={contactPage}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

const mapStateToProps = (state) => ({
    user: state.userReducer
})

export default withRouter(connect(mapStateToProps)(InvestorProfileHeader))
